import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "@src/components/PageTitle";
import WrapperContainer from "@src/components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection, {
  TopBarColumn,
} from "@src/components/PageTopSection/PageTopSection";
import { theme } from "@src/theme";
import { useDebounce } from "@src/hooks/useDebounce";
import { ICuratorConversationStatus } from "@src/types/curator";
import {
  FIGMA_MESSAGES_HEADER_BG,
  TabButton,
  TabsContainer,
} from "../Messages/MessagesPage.styles";
import { getConversationId } from "../Messages/conversationUtils";
import {
  ADMIN_CONVERSATIONS_PAGE_SIZE,
  useAdminConversationsQuery,
} from "@src/queries/adminCuratorConversationQueries";
import AdminConversationList from "./AdminConversationList";
import AdminTranscript from "./AdminTranscript";
import {
  FiltersRow,
  HeaderControlsColumn,
  HeaderGrid,
  PagerButton,
  PaginationRow,
  SearchInput,
  Subtitle,
} from "./AdminMessages.styles";

type StatusFilter = ICuratorConversationStatus | "all";

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "requested", label: "Requests" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
  { value: "blocked", label: "Blocked" },
  { value: "ai_blocked", label: "AI Blocked" },
];

const AdminMessagesPage = (): React.JSX.Element => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  const search = useDebounce(searchInput.trim(), 400);

  // A filter change re-pages from the start; keeping the old page would show an
  // empty list whenever the narrower result set has fewer pages.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const params = useMemo(
    () => ({
      ...(statusFilter === "all" ? {} : { status: statusFilter }),
      ...(search ? { search } : {}),
      page,
      pageSize: ADMIN_CONVERSATIONS_PAGE_SIZE,
    }),
    [statusFilter, search, page]
  );

  const { data, isPending, isError } = useAdminConversationsQuery(params);

  const conversations = data?.conversations ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const pageCount = pagination?.pageCount ?? 0;
  const rangeStart =
    total === 0 ? 0 : (page - 1) * ADMIN_CONVERSATIONS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * ADMIN_CONVERSATIONS_PAGE_SIZE, total);

  const selectedConversation = conversations.find(
    (conversation) => getConversationId(conversation) === selectedConversationId
  );

  // The selected conversation can fall off the current page (filter, search or
  // paging), and a transcript with no conversation header reads as a bug.
  useEffect(() => {
    if (!selectedConversationId || isPending) return;
    const stillListed = conversations.some(
      (conversation) =>
        getConversationId(conversation) === selectedConversationId
    );
    if (!stillListed) {
      setSelectedConversationId(null);
    }
  }, [conversations, isPending, selectedConversationId]);

  return (
    <WrapperContainer noScroll>
      {/* autoHeight: six status chips wrap to a second row on a narrow window,
          and the fixed-height header would otherwise clip the page title and
          the wrapped row. [ROVR-4029] */}
      <PageTopSection backgroundColor={FIGMA_MESSAGES_HEADER_BG} autoHeight>
        <HeaderGrid columnCount={2}>
          <TopBarColumn>
            <PageTitle>All Messages</PageTitle>
            <Subtitle>Every curator conversation, read-only.</Subtitle>
            <TabsContainer>
              {STATUS_FILTERS.map(({ value, label }) => (
                <TabButton
                  key={value}
                  $active={statusFilter === value}
                  onClick={() => setStatusFilter(value)}
                >
                  {label}
                </TabButton>
              ))}
            </TabsContainer>
          </TopBarColumn>
          <HeaderControlsColumn>
            <FiltersRow>
              <SearchInput
                placeholder="Search curator, listener or subject"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </FiltersRow>
            <PaginationRow>
              <PagerButton
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page <= 1 || isPending}
              >
                Previous
              </PagerButton>
              <span>
                {total === 0
                  ? "No conversations"
                  : `${rangeStart}–${rangeEnd} of ${total}`}
              </span>
              <PagerButton
                onClick={() => setPage((current) => current + 1)}
                disabled={isPending || pageCount === 0 || page >= pageCount}
              >
                Next
              </PagerButton>
            </PaginationRow>
          </HeaderControlsColumn>
        </HeaderGrid>
      </PageTopSection>

      <ContentContainer
        contentContainerStyles={{
          borderTop: `1px solid ${theme.colors.darkGray}`,
          flexDirection: "row",
          minHeight: 0,
        }}
      >
        <AdminConversationList
          conversations={conversations}
          selectedConversationId={selectedConversationId}
          onSelectConversation={(conversationId) =>
            setSelectedConversationId((current) =>
              current === conversationId ? null : conversationId
            )
          }
          isLoading={isPending}
          isError={isError}
        />
        <AdminTranscript
          conversationId={selectedConversationId}
          conversation={selectedConversation}
        />
      </ContentContainer>
    </WrapperContainer>
  );
};

export default AdminMessagesPage;
